import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Bookmark, BookmarkCheck, Mail, TrendingUp, Star } from "lucide-react";
import { PageHero, CtaBand } from "../components/site/kit";
import { NextSteps, Pager } from "../components/site/extras";
import { Reveal } from "../components/site/motion";
import { useBlogs } from "../lib/blogsApi";
import { useBookmarks } from "../lib/bookmarks";
import { track } from "../lib/analytics";
import { useSite } from "../context/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITUATIONS = ["Growth Has Stalled", "Ready to Scale", "Reposition & Transform", "Create Enterprise Value"];

const BlogCard = ({ b, i, saved, onToggle, topRead }) => (
  <Reveal delay={(i % 3) * 0.06}>
    <div className={`group relative flex h-full flex-col overflow-hidden border bg-white transition-colors duration-200 hover:border-[#5f8f10] ${b.featured ? "border-[#5f8f10] ring-1 ring-[#5f8f10]" : "border-black/15"}`}>
      <button
        type="button"
        data-testid={`insight-save-${b.slug}`}
        aria-label={saved ? "Remove from reading list" : "Save to reading list"}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); track("blog_bookmark", { slug: b.slug, saved: !saved }); onToggle(b.slug); }}
        className={`absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center border transition-colors duration-200 ${saved ? "border-[#5f8f10] bg-[#5f8f10] text-[#161A15]" : "border-white/40 bg-black/40 text-white hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
      >
        {saved ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
      </button>
      <Link to={`/insights/${b.slug}`} data-testid={`insight-${b.slug}`} className="flex h-full flex-col">
        <div className="relative overflow-hidden">
          <img src={b.image} alt="" loading="lazy" decoding="async" className="aspect-[16/9] w-full object-cover grayscale transition-all duration-500 group-hover:scale-[1.03] group-hover:grayscale-0" />
          <span className="font-display absolute left-5 top-4 text-3xl leading-none text-[#5f8f10] md:text-4xl">{b.number}</span>
          <div className="absolute bottom-3 left-3 flex flex-wrap items-center gap-2">
            {b.featured && (
              <span data-testid="featured-badge" className="inline-flex items-center gap-1.5 border border-[#5f8f10] bg-[#161A15] px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-[#5f8f10]">
                <Star size={11} /> Featured
              </span>
            )}
            {topRead && (
              <span data-testid="most-read-badge" className="inline-flex items-center gap-1.5 bg-[#5f8f10] px-2.5 py-1 font-body text-[10px] font-semibold uppercase tracking-[0.16em] text-[#161A15]">
                <TrendingUp size={11} /> Most read
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-1 flex-col p-6">
          <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-black/40">{b.tags.join(" · ")}</span>
          <h3 className="font-display mt-3 text-xl uppercase leading-[1.05] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10] md:text-2xl">{b.title}</h3>
          <p className="font-body mt-3 text-sm font-normal leading-relaxed text-black/60">{b.excerpt}</p>
          <div className="mt-auto flex items-center justify-between pt-5">
            <span className="font-body inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
              Read <span aria-hidden className="transition-transform duration-200 group-hover:translate-x-1">→</span>
            </span>
            <span className="font-body text-xs font-semibold uppercase tracking-[0.12em] text-black/35">{b.readTime}</span>
          </div>
        </div>
      </Link>
    </div>
  </Reveal>
);

const EmailListForm = ({ slugs }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    track("reading_list_emailed", { count: slugs.length });
    try {
      const res = await fetch(`${API}/insights/email-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, slugs }),
      });
      if (!res.ok) { setError("Please enter a valid email."); return; }
      setDone(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="reading-list-email" className="mb-10 border border-[#161A15] bg-[#161A15] p-6 text-white md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <Mail size={20} className="text-[#5f8f10]" />
          <div>
            <h3 className="font-display text-xl uppercase tracking-tight md:text-2xl">Send this list to your inbox</h3>
            <p className="font-body mt-1 text-sm font-normal text-white/60">Keep your saved reading close. We will send these {slugs.length} {slugs.length === 1 ? "article" : "articles"} to your email.</p>
          </div>
        </div>
        {done ? (
          <p data-testid="reading-list-email-confirm" className="font-body text-base font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">On its way to your inbox.</p>
        ) : (
          <form onSubmit={submit} className="flex w-full max-w-sm flex-col md:w-auto">
            <div className="flex">
              <input
                type="email"
                required
                data-testid="reading-list-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="font-body w-full border border-white/25 bg-transparent px-4 py-3 text-base font-normal text-white outline-none transition-colors placeholder:text-white/40 focus:border-[#5f8f10] md:w-64"
              />
              <button type="submit" data-testid="reading-list-email-submit" className="font-body shrink-0 border border-[#5f8f10] bg-[#5f8f10] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors hover:border-[#4a700c] hover:bg-[#4a700c]">
                Send
              </button>
            </div>
            {error && <p className="font-body mt-2 text-sm text-red-400">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
};

export default function Insights() {
  const { openTalk } = useSite();
  const blogs = useBlogs();
  const { saved, toggle, isSaved } = useBookmarks();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState("all");
  const [situation, setSituationState] = useState(SITUATIONS.includes(searchParams.get("situation")) ? searchParams.get("situation") : null);
  const [topSlug, setTopSlug] = useState(null);

  useEffect(() => {
    fetch(`${API}/insights/popular?limit=1`)
      .then((r) => r.json())
      .then((d) => {
        const top = (d.blogs || [])[0];
        if (top && top.views > 0) setTopSlug(top.slug);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const p = searchParams.get("situation");
    setSituationState(SITUATIONS.includes(p) ? p : null);
    if (p) setView("all");
  }, [searchParams]);

  const setSituation = (val) => {
    setSituationState(val);
    if (val) setSearchParams({ situation: val }); else setSearchParams({});
  };

  let list;
  if (view === "saved") {
    list = blogs.filter((b) => saved.includes(b.slug));
  } else if (situation) {
    list = blogs.filter((b) => b.situation === situation);
  } else {
    list = [...blogs];
    const fi = list.findIndex((b) => b.featured);
    if (fi > 0) list.unshift(list.splice(fi, 1)[0]);
  }

  const Tab = ({ id, label }) => {
    const active = view === id;
    return (
      <button
        type="button"
        data-testid={`switchboard-tab-${id}`}
        onClick={() => setView(id)}
        aria-pressed={active}
        className={`font-body border px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${active ? "border-[#5f8f10] bg-[#5f8f10] text-white" : "border-black/20 bg-transparent text-black/60 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
      >
        {label}
      </button>
    );
  };

  const Chip = ({ value, label }) => {
    const active = situation === value;
    return (
      <button
        type="button"
        data-testid={`switchboard-situation-${value ? value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") : "all"}`}
        onClick={() => setSituation(value)}
        aria-pressed={active}
        className={`font-body border px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] transition-colors duration-200 ${active ? "border-[#5f8f10] bg-[#5f8f10] text-white" : "border-black/15 bg-transparent text-black/55 hover:border-[#5f8f10] hover:text-[#5f8f10]"}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="font-body bg-white">
      <PageHero
        testId="insights-hero"
        eyebrow="Switchboard"
        crumbs={[{ label: "Home", href: "/" }, { label: "Switchboard" }]}
        title={<>Thinking worth<br />your <span className="text-[#5f8f10]">time.</span></>}
        subcopy="Practical perspectives on growth, scale and transformation from the SWITCH team."
      />

      <section className="bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-16 md:px-12 md:py-24 lg:px-16">
          <div className="mb-6 flex flex-wrap items-center gap-2.5">
            <Tab id="all" label="All articles" />
            <Tab id="saved" label={`Reading list${saved.length ? ` (${saved.length})` : ""}`} />
          </div>

          {view === "all" && (
            <div data-testid="switchboard-situation-chips" className="mb-10 flex flex-wrap items-center gap-2 border-b border-black/10 pb-8">
              <span className="font-body mr-1 text-xs font-semibold uppercase tracking-[0.12em] text-black/35">By growth situation</span>
              <Chip value={null} label="All" />
              {SITUATIONS.map((s) => <Chip key={s} value={s} label={s} />)}
            </div>
          )}

          {view === "saved" && list.length > 0 && <EmailListForm slugs={saved} />}

          {list.length === 0 ? (
            <div data-testid="switchboard-empty" className="border border-dashed border-black/20 bg-white px-8 py-20 text-center">
              <h3 className="font-display text-2xl uppercase tracking-tight text-black">{view === "saved" ? "Your reading list is empty" : "No articles in this situation yet"}</h3>
              <p className="font-body mx-auto mt-3 max-w-md text-base font-normal text-black/60">{view === "saved" ? "Tap the bookmark on any article to save it here and pick up where you left off." : "Try another growth situation or browse all articles."}</p>
              <button type="button" onClick={() => { setView("all"); setSituation(null); }} className="font-body mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                Browse all articles <span aria-hidden>→</span>
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {list.map((b, i) => (
                <BlogCard key={b.slug} b={b} i={i} saved={isSaved(b.slug)} onToggle={toggle} topRead={b.slug === topSlug} />
              ))}
            </div>
          )}
        </div>
      </section>

      <NextSteps links={[
        { eyebrow: "What we solve", title: "Four ways growth gets stuck", blurb: "Find the growth situation that sounds like your business.", href: "/what-we-solve" },
        { eyebrow: "How we work", title: "The SWITCH model", blurb: "The four moves behind our thinking.", href: "/how-we-work" },
        { eyebrow: "Our work", title: "Proof, not promises", blurb: "See the thinking applied to real businesses.", href: "/our-work" },
      ]} />

      <Pager current="/insights" />

      <CtaBand
        heading="Have a growth problem worth solving?"
        onClick={() => openTalk()}
        testId="insights-cta"
      />
    </div>
  );
}
